import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DeviceVaultProvider, useDeviceVault } from './contexts/DeviceVaultContext';
import { OfflineAccessPage } from './components/auth/OfflinePin';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoginPage from './components/auth/LoginPage';
import LoginSuccess from './components/auth/LoginSuccess';
import LoginError from './components/auth/LoginError';
import Dashboard from './pages/Dashboard';
import { MyStudentsView, AllStudentsView, AddStudentView } from './components/students/StudentComponents';
import { SessionReports } from './components/sessions/SessionReports';
import AdminSection from './components/admin/AdminSection';  // Import the AdminSection component
import InvoicingDashboard from './components/invoicing/InvoicingDashboard';
import ManageRetreats from './components/admin/ManageRetreats';
import MetricsSection from './components/metrics/MetricsSection';
import InitialSurveyForm from './components/surveys/InitialSurveyForm';
import FinalSurveyForm from './components/surveys/FinalSurveyForm';
import UpdatePrompt from './components/common/UpdatePrompt';
import AssessmentsPage from './components/assessments/AssessmentsPage';
import AssessmentFormPage from './components/assessments/AssessmentFormPage';
import OfflineAccessAdminPage from './components/assessments/OfflineAccessPage';

const queryClient = new QueryClient();

// The roles that existed before 'wmf'. A metrics-only user must not reach a
// page that lists students or session reports: the API refuses them now, and
// without this they would get a rendered page full of failed requests instead
// of being told plainly that the section is not theirs.
const STAFF_ROLES = ['admin', 'manager', 'therapist'];

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <DeviceVaultProvider>
        <div className="min-h-screen bg-slate-50">
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LoginPage />} />
            <Route path="/login-success" element={<LoginSuccess />} />
            <Route path="/login-error" element={<LoginError />} />
            <Route path="/offline" element={<OfflineAccessPage />} />
            <Route path="/surveys/initial/:token" element={<InitialSurveyForm />} />
            <Route path="/surveys/final/:token" element={<FinalSurveyForm />} />

            {/* Protected dashboard routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute allowOffline>
                <Dashboard />
              </ProtectedRoute>
            }>
              {/* Nested routes for dashboard */}
              <Route index element={<DashboardLanding />} />
              <Route path="students/my-students" element={
                <ProtectedRoute allowedRoles={STAFF_ROLES}>
                  <MyStudentsView />
                </ProtectedRoute>
              } />
              <Route path="students/all" element={
                <ProtectedRoute allowedRoles={STAFF_ROLES}>
                  <AllStudentsView />
                </ProtectedRoute>
              } />
              <Route path="students/add" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                  <AddStudentView />
                </ProtectedRoute>
              } />
              <Route path="reports">
                <Route index element={<Navigate to="view" replace />} />
                <Route path="create" element={
                  <ProtectedRoute allowedRoles={STAFF_ROLES}>
                    <SessionReports.Create />
                  </ProtectedRoute>
                } />
                <Route path="view" element={
                  <ProtectedRoute allowedRoles={STAFF_ROLES}>
                    <SessionReports.View />
                  </ProtectedRoute>
                } />
              </Route>
              <Route path="assessments">
                <Route index element={
                  <ProtectedRoute allowedRoles={STAFF_ROLES} allowOffline>
                    <AssessmentsPage />
                  </ProtectedRoute>
                } />
                <Route path="offline-access" element={
                  <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <OfflineAccessAdminPage />
                  </ProtectedRoute>
                } />
                <Route path=":uuid" element={
                  <ProtectedRoute allowedRoles={STAFF_ROLES} allowOffline>
                    <AssessmentFormPage />
                  </ProtectedRoute>
                } />
              </Route>
              <Route path="invoicing">
                <Route index element={
                  <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <InvoicingDashboard />
                  </ProtectedRoute>
                } />
                <Route path="retreats" element={
                  <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <ManageRetreats />
                  </ProtectedRoute>
                } />
              </Route>
              <Route path="metrics/*" element={
                <ProtectedRoute allowedRoles={['admin', 'manager', 'therapist', 'wmf']}>
                  <MetricsSection />
                </ProtectedRoute>
              } />
              <Route path="admin/*" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminSection />
                </ProtectedRoute>
              } />
            </Route>
            {/* Catch all redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <UpdatePrompt />
        </div>
        </DeviceVaultProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

// Where /dashboard sends you depends on the role. The 'wmf' role has no
// access to students or session reports, so the old unconditional redirect to
// the student list would have dropped it straight onto an Access Denied page.
const DashboardLanding = () => {
  const { user } = useAuth();
  const vault = useDeviceVault();
  let destination = user?.role === 'wmf' ? 'metrics' : 'students/my-students';
  if (!user && vault.offline) destination = 'assessments';
  return <Navigate to={destination} replace />;
};

export default App;
